import * as microsoftTeams from "@microsoft/teams-js";
import { logger } from './logger';

type TeamsAppContext = Awaited<ReturnType<typeof microsoftTeams.app.getContext>>;

export interface TeamsContextState {
  isInitialized: boolean;
  isInTeams: boolean;
  isMobileTeams: boolean;
  hostClientType: string | null;
  /** The M365 host the app was launched in (`Teams` / `Outlook` / `Office`), or null outside an M365 host. */
  hostName: string | null;
  /**
   * Whether the SPA is rendered inside a cross-origin frame. Nothing else legitimately
   * frames this app, so this is the one host signal that does not depend on the SDK
   * handshake completing, on the deployment domain, or on a recognisable user agent.
   * Callers use it to refuse browser-only flows (MSAL popups, the marketing landing
   * page) that cannot work inside an M365 host webview.
   */
  isEmbedded: boolean;
  theme: string | null;
  rawContext: TeamsAppContext | null;
}

/**
 * TeamsJS handshake budgets. These were originally 5s/3s, which a cold mobile webview
 * on a cellular connection routinely overruns — the app then fell back to heuristics
 * and concluded it was a plain browser tab. Be generous: overrunning the budget means
 * a degraded experience, so it must only trigger when the host is genuinely absent.
 */
const SDK_INITIALIZE_TIMEOUT_MS = 15000;
const SDK_CONTEXT_TIMEOUT_MS = 8000;

function hasTeamsUserAgent(userAgent: string): boolean {
  return userAgent.includes('teamsmobile') ||
    userAgent.includes('teams-mobile') ||
    userAgent.includes('teams/') ||
    userAgent.includes('msteams') ||
    userAgent.includes('skypeteams');
}

/**
 * Whether this document is framed by another origin.
 *
 * This is the primary fallback signal when the TeamsJS handshake does not complete.
 * It deliberately does not look at the user agent (Outlook and Microsoft 365 mobile
 * send no Teams token) nor at the hostname (the previous implementation only
 * recognised the dev `*.azurestaticapps.net` domain, so production could never
 * detect a host at all). Reading `window.top` across origins can throw under some
 * sandbox policies; a throw itself proves we are cross-origin framed.
 */
function isCrossOriginFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function normalizeTheme(theme: string | null | undefined): 'light' | 'dark' {
  return theme === 'dark' || theme === 'contrast' ? 'dark' : 'light';
}

class TeamsAuthService {
  private initialized = false;
  private isInTeams = false;
  private isMobileTeams = false;
  private hostClientType: string | null = null;
  private hostName: string | null = null;
  private embedded = false;
  private theme: string | null = null;
  private rawContext: TeamsAppContext | null = null;
  private initializationPromise: Promise<TeamsContextState> | null = null;

  private buildState(): TeamsContextState {
    return {
      isInitialized: this.initialized,
      isInTeams: this.isInTeams,
      isMobileTeams: this.isMobileTeams,
      hostClientType: this.hostClientType,
      hostName: this.hostName,
      isEmbedded: this.embedded,
      theme: this.theme,
      rawContext: this.rawContext,
    };
  }

  // Tracks whether we've already told the Teams host that loading finished, so
  // the native loading indicator (manifest `showLoadingIndicator: true`) is
  // dismissed exactly once. Without a call here the shimmer stays up forever.
  private notifiedHost = false;

  /**
   * Dismiss the host's native loading indicator.
   *
   * The manifest opts into `showLoadingIndicator: true`, which makes Teams, Outlook
   * and the Microsoft 365 app show their own shimmer over the tab until the app
   * reports readiness. EVERY init path must reach this — including the ones that
   * conclude we are not in a host at all.
   *
   * This used to bail out when `isInTeams` was false, which meant a failed handshake
   * inside Outlook/Microsoft 365 mobile sent neither notifySuccess nor notifyFailure
   * and the host sat behind its shimmer indefinitely: the app looked like it never
   * loaded. Outside a host the notify calls simply throw (the SDK was never
   * initialized) and are swallowed below, so calling unconditionally is safe.
   *
   * Guarded so an SDK hiccup can never crash init, and idempotent so the
   * double-notify APIs are only ever called once.
   */
  private notifyHostLoaded(success: boolean): void {
    if (this.notifiedHost) {
      return;
    }
    try {
      if (success) {
        microsoftTeams.app.notifySuccess();
      } else {
        microsoftTeams.app.notifyFailure({
          reason: microsoftTeams.app.FailedReason.Other,
        });
      }
      this.notifiedHost = true;
    } catch (error) {
      // notify* throws if initialize() never completed — the normal case for a
      // plain browser tab. Nothing to recover; a real host will fall back to its
      // own load timeout.
      logger.debug('Host notify to dismiss loading indicator failed', { success, error: String(error) }, 'TEAMS');
    }
  }

  private applyContext(context: TeamsAppContext): void {
    this.rawContext = context;
    this.hostClientType = context.app?.host?.clientType ?? null;
    this.hostName = context.app?.host?.name ?? null;
    this.theme = context.app?.theme ?? null;
    this.isMobileTeams = this.hostClientType === 'android' || this.hostClientType === 'ios';
  }

  // Allow manual override for testing
  forceTeamsMode(enabled: boolean = true): void {
    logger.debug('Manual override: Force Teams mode', { enabled }, 'TEAMS');
    this.isInTeams = enabled;
    this.isMobileTeams = false;
    this.hostClientType = null;
    this.hostName = null;
    this.embedded = false;
    this.theme = null;
    this.rawContext = null;
    this.initialized = true;
  }

  async initialize(): Promise<boolean> {
    const state = await this.initializeContext();
    return state.isInTeams;
  }

  async initializeContext(): Promise<TeamsContextState> {
    if (this.initialized) {
      return this.buildState();
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = (async () => {
      // Check for Teams BEFORE trying SDK
      console.log('=== TEAMS DETECTION START ===');
      console.log('User Agent:', navigator.userAgent);
      console.log('Current URL:', window.location.href);
      console.log('In iframe?', isCrossOriginFramed());
      console.log('Teams SDK loaded?', typeof microsoftTeams !== 'undefined');

      const userAgent = navigator.userAgent.toLowerCase();
      const isTeamsUserAgent = hasTeamsUserAgent(userAgent);
      const inIframe = isCrossOriginFramed();
      // Record this before the SDK race so the flag is available to callers even
      // when the handshake later fails or hangs.
      this.embedded = inIframe;
      let inTeamsUrl = false;
      try {
        const currentHost = new URL(window.location.href).hostname.toLowerCase();
        inTeamsUrl = currentHost === 'teams.microsoft.com' || currentHost.endsWith('.teams.microsoft.com');
      } catch {
        inTeamsUrl = false;
      }

      console.log('Pre-detection:', {
        isTeamsUserAgent,
        inIframe,
        inTeamsUrl,
      });

      if (isTeamsUserAgent) {
        console.log('🎯 TEAMS DETECTED BY USER AGENT - Initializing SDK...');
        this.isInTeams = true;
      }

      try {
        console.log('Teams SDK: Starting initialization...');

        const initPromise = microsoftTeams.app.initialize();
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Teams SDK initialization timeout')), SDK_INITIALIZE_TIMEOUT_MS);
        });

        await Promise.race([initPromise, timeoutPromise]);
        console.log('Teams SDK: Initialized successfully');

        const contextPromise = microsoftTeams.app.getContext();
        const contextTimeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Teams context timeout')), SDK_CONTEXT_TIMEOUT_MS);
        });

        const context = await Promise.race([contextPromise, contextTimeoutPromise]);
        this.applyContext(context);

        if (!this.isInTeams) {
          this.isInTeams = !!(
            context.app?.host?.name ||
            context.page?.frameContext === 'content' ||
            (() => {
              try {
                const hostname = new URL(window.location.href).hostname.toLowerCase();
                return hostname === 'teams.microsoft.com' || hostname.endsWith('.teams.microsoft.com');
              } catch {
                return false;
              }
            })()
          );
        }

        this.initialized = true;

        console.log('✅ Teams SDK initialized successfully', {
          isInTeams: this.isInTeams,
          host: context.app?.host?.name,
          frameContext: context.page?.frameContext,
          clientType: this.hostClientType,
          detectedBy: isTeamsUserAgent ? 'UserAgent' : 'Context',
        });

        // SDK handshake succeeded — clear the native loading indicator.
        this.notifyHostLoaded(true);

        return this.buildState();
      } catch (error) {
        console.log('SDK initialization failed, using fallback detection:', error);

        let fallbackInTeamsUrl = false;
        try {
          const hostname = new URL(window.location.href).hostname.toLowerCase();
          fallbackInTeamsUrl = hostname === 'teams.microsoft.com' || hostname.endsWith('.teams.microsoft.com');
        } catch {
          fallbackInTeamsUrl = false;
        }

        // Being framed by another origin is the decisive signal. Only an M365 host
        // embeds this app, and unlike the user agent (Outlook/Microsoft 365 mobile
        // carry no Teams token) or the hostname (the old check only recognised the
        // dev `*.azurestaticapps.net` domain, so production always fell through to
        // "plain browser") it holds on every host and platform.
        const strongEvidence = isTeamsUserAgent || inIframe || fallbackInTeamsUrl;

        this.isInTeams = strongEvidence;
        this.isMobileTeams = strongEvidence && /(android|iphone|ipad|ipod|ios)/i.test(userAgent);
        this.hostClientType = this.isMobileTeams
          ? (userAgent.includes('android') ? 'android' : 'ios')
          : null;
        this.theme = null;
        this.rawContext = null;
        this.initialized = true;

        console.log('🔍 M365 host detection fallback:', {
          userAgent: navigator.userAgent,
          isTeamsUserAgent,
          inIframe,
          inTeamsUrl: fallbackInTeamsUrl,
          strongEvidence,
          finalDecision: this.isInTeams,
        });

        if (this.isInTeams) {
          console.log('✅ M365 HOST DETECTED by fallback!');
        } else {
          console.log('❌ No M365 host - web browser detected');
        }

        // Always report, on every path. When we believe we are in a host the
        // handshake failed, so this is a genuine init error and releases a loader
        // that would otherwise hang forever. When we are not in a host the SDK was
        // never initialized and the call is swallowed.
        this.notifyHostLoaded(false);

        return this.buildState();
      }
    })();

    try {
      return await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * Whether host SSO should be attempted rather than a browser sign-in flow.
   *
   * Deliberately broader than `isInTeams`: if we are framed we are in an M365 host
   * even when the handshake failed to confirm which one. Browser flows (MSAL popup)
   * cannot work inside a host webview, so attempting host SSO and surfacing a real
   * error beats silently opening a popup that is blocked.
   */
  isInM365Host(): boolean {
    return this.isInTeams || this.embedded;
  }

  /**
   * Dismiss the host's loading indicator from outside the normal init path.
   *
   * `initializeContext()` already reports on every path it reaches, but it only
   * runs once the React tree mounts. Callers that sit *outside* that tree — the
   * bootstrap's MSAL failure branch and the top-level error boundary — use this
   * so a crash surfaces as a visible error instead of leaving Outlook and the
   * Microsoft 365 app behind their shimmer forever. Idempotent and never throws.
   */
  releaseLoadingIndicator(success: boolean = false): void {
    this.notifyHostLoaded(success);
  }

  async getAuthToken(): Promise<string> {
    if (!this.isInM365Host()) {
      throw new Error('Not running in a Microsoft 365 host context');
    }

    try {
      logger.debug('Teams SSO: Requesting auth token', undefined, 'TEAMS');
      
      // Use Teams SSO to get token - SDK v2 API
      // This should work on both mobile and desktop Teams
      const token = await microsoftTeams.authentication.getAuthToken();
      
      logger.debug('Teams SSO: Token received successfully', { tokenLength: token.length }, 'TEAMS');
      
      // For debugging in development only - decode token to verify audience
      if (import.meta.env.DEV) {
        const parts = token.split('.');
        if (parts.length >= 2 && parts[1]) {
          try {
            const payload = JSON.parse(atob(parts[1]));
            logger.debug('Token details', { audience: payload.aud, scope: payload.scp }, 'TEAMS');
          } catch (parseError) {
            logger.warn('Could not parse token for debugging', parseError, 'TEAMS');
          }
        }
      }
      
      return token;
    } catch (error: any) {
      logger.error('Teams authentication failed', error, 'TEAMS');
      
      // Provide more helpful error messages
      if (error.message?.includes('CancelledByUser')) {
        throw new Error('Authentication was cancelled. Please try again.');
      } else if (error.message?.includes('FailedToOpenWindow')) {
        throw new Error('Could not open authentication window. Please check your Teams settings.');
      } else if (error.message?.includes('ConsentRequired')) {
        console.log('Consent required, attempting interactive consent...');
        // Try to open consent dialog
        try {
          const consentToken = await this.getConsentToken();
          return consentToken;
        } catch (consentError) {
          throw new Error('Admin consent is required. Please contact your administrator.');
        }
      }
      
      throw new Error(`Teams authentication failed: ${error.message || 'Unknown error'}`);
    }
  }

  private async getConsentToken(): Promise<string> {
    // Open authentication dialog for consent
    console.log('Opening Teams authentication dialog for consent...');
    
    const authUrl = `${window.location.origin}/auth-start.html`;
    
    return new Promise((resolve, reject) => {
      microsoftTeams.authentication.authenticate({
        url: authUrl,
        width: 600,
        height: 535
      }).then((result) => {
        console.log('Teams authentication dialog completed');
        if (result) {
          resolve(result);
        } else {
          reject(new Error('No token received from authentication dialog'));
        }
      }).catch((reason) => {
        console.error('Teams authentication dialog failed:', reason);
        reject(new Error(`Authentication dialog failed: ${reason}`));
      });
    });
  }

  async getUserInfo(): Promise<any> {
    if (!this.isInM365Host()) {
      throw new Error('Not running in a Microsoft 365 host context');
    }

    try {
      const context = await this.getContext();
      if (!context) {
        throw new Error('Teams context unavailable');
      }
      return {
        id: context.user?.id,
        displayName: context.user?.displayName,
        userPrincipalName: context.user?.userPrincipalName,
        tenantId: context.user?.tenant?.id,
        locale: context.app.locale
      };
    } catch (error) {
      console.error('Failed to get Teams user info:', error);
      throw error;
    }
  }

  async getContext(): Promise<TeamsAppContext | null> {
    const state = await this.initializeContext();
    if (!state.isInTeams && !state.isEmbedded) {
      return null;
    }

    if (this.rawContext) {
      return this.rawContext;
    }

    try {
      const context = await microsoftTeams.app.getContext();
      this.applyContext(context);
      return context;
    } catch (error) {
      logger.warn('Failed to refresh Teams context', error, 'TEAMS');
      return null;
    }
  }

  async getTheme(): Promise<'light' | 'dark'> {
    const context = await this.getContext();
    return normalizeTheme(context?.app?.theme ?? this.theme);
  }

  registerOnThemeChangeHandler(handler: (theme: 'light' | 'dark') => void): void {
    if (!this.isInM365Host()) {
      return;
    }

    try {
      microsoftTeams.app.registerOnThemeChangeHandler((theme) => {
        this.theme = theme;
        handler(normalizeTheme(theme));
      });
    } catch (error) {
      // Throws when we believed we were framed by a host but the SDK never
      // initialized. Theme sync is cosmetic — never let it break rendering.
      logger.debug('Host theme-change handler registration failed', { error: String(error) }, 'TEAMS');
    }
  }

  getContextState(): TeamsContextState {
    return this.buildState();
  }

  getIsInTeams(): boolean {
    return this.isInTeams;
  }

  getIsMobileTeams(): boolean {
    return this.isMobileTeams;
  }
}

// Export singleton instance
export const teamsAuth = new TeamsAuthService();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).teamsAuth = teamsAuth;
  (window as any).forceTeamsMode = () => {
    teamsAuth.forceTeamsMode(true);
    console.log('✅ Teams mode forced! Reload the page.');
  };
}

// Hook for React components
export function useTeamsAuth() {
  return {
    initialize: () => teamsAuth.initialize(),
    getAuthToken: () => teamsAuth.getAuthToken(),
    getUserInfo: () => teamsAuth.getUserInfo(),
    isInTeams: teamsAuth.getIsInTeams()
  };
}
